/**
 * RPS-SCOPE-PICKER-MODAL
 * Choix des correspondances d'un établissement ou d'une fonction : les filtres du tableau de bord
 * sur l'enquête retenue, et dessous les résultats RPS du périmètre en cours de sélection.
 * Les deux blocs sont les widgets du tableau de bord, montés tels quels.
 */
import ListDropdown from "@/components/list-dropdown"
import Modal from "@/components/modal"
import PageLoader from "@/components/page-loader"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { store } from "@/index"
import { attributeGet } from "@/redux/attribute.actions"
import { Attribute } from "@/redux/attribute.types"
import { DashboardFilters, Filter, FilterLite } from "@/redux/filter.types"
import { filterReplaceDashboard } from "@/redux/filter.actions"
import { STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import { surveyActivate } from "@/redux/survey.actions"
import { Survey } from "@/redux/survey.types"
import fetchArchiveAttributes from "@/utils/fetch-archive-attributes.utils"
import fetchArchiveFilters from "@/utils/fetch-archive-filters.utils"
import DashboardFiltersWidget from "@/widgets/dashboard-filters.widget"
import DashboardRpsOverviewWidget from "@/widgets/dashboard-rps-overview.widget"
import { useEffect, useRef, useState } from "react"

interface Props {
  entityName: string
  //Périmètre hérité de l'établissement : croisé (ET) avec celui de la fonction côté serveur, il est
  //donc appliqué ici pour que les chiffres soient les vrais, mais il ne s'édite pas et ne se sauve pas
  lockedAttributeIds: string[]
  selectedAttributeIds: string[]
  surveys: Survey[]
  onClose: () => void
  onValidate: (attributeIds: string[]) => void
}

//Les filtres du tableau de bord se décrivent par filtre, les attributs retenus se regroupent donc
//sous le nom de leur filtre
function createDashboardFilters(attributes: Attribute[]) {
  const customFilters = attributes.reduce<FilterLite[]>((filters, attribute) => {
    if (!attribute.filterName) return filters

    const existing = filters.find((filter) => filter.id === attribute.filterName)
    if (existing) {
      existing.attributesIds.push(attribute.id)
      return filters
    }

    return [...filters, new FilterLite({ id: attribute.filterName, attributesIds: [attribute.id] })]
  }, [])

  return new DashboardFilters({ customFilters })
}

export default function RpsScopePickerModal(props: Props) {
  const dispatch = useAppDispatch()

  //Les widgets travaillent sur l'enquête active et les filtres du tableau de bord : on les emprunte
  //le temps de la modale, et on les rend en sortant
  const currentDashboardFilters = useAppSelector((state) => state.filter.dashboard)
  const currentSurvey = useAppSelector((state) => state.survey.active)
  const currentAttributes = useAppSelector((state) => state.attribute.list)
  const previousDashboardFilters = useRef(currentDashboardFilters)
  const previousSurvey = useRef(currentSurvey)
  const previousAttributes = useRef(currentAttributes)

  const [survey, setSurvey] = useState<Survey>(props.surveys[0])
  const [isLoading, setIsLoading] = useState(true)
  const [filters, setFilters] = useState<Filter[]>([])
  //Les filtres qui portent le périmètre de l'établissement, verrouillés dans l'en-tête
  const [lockedFilterIds, setLockedFilterIds] = useState<string[]>([])

  useEffect(() => {
    let isMounted = true

    async function loadSurveyFilters() {
      setIsLoading(true)

      const surveyFilters = await fetchArchiveFilters(new DashboardFilters(), survey.id)
      const surveyAttributes = await fetchArchiveAttributes(new DashboardFilters(), survey.id, null)
      //Une correspondance posée sur une autre enquête n'a pas d'équivalent ici : elle est ignorée
      const locked = surveyAttributes.filter((attribute) => props.lockedAttributeIds.includes(attribute.id))
      const selected = surveyAttributes.filter((attribute) => props.selectedAttributeIds.includes(attribute.id))

      if (!isMounted) return

      setFilters(surveyFilters)
      setLockedFilterIds([...new Set(locked.map((attribute) => attribute.filterName))])
      dispatch(attributeGet(surveyAttributes))
      dispatch(surveyActivate(survey))
      dispatch(filterReplaceDashboard(createDashboardFilters([...locked, ...selected])))
      setIsLoading(false)
    }

    loadSurveyFilters()

    return () => {
      isMounted = false
    }
  }, [dispatch, props.lockedAttributeIds, props.selectedAttributeIds, survey])

  //Le tableau de bord retrouve son enquête et ses filtres quand la modale se ferme
  useEffect(() => {
    const attributes = previousAttributes.current
    const dashboardFilters = previousDashboardFilters.current
    const activeSurvey = previousSurvey.current

    return () => {
      dispatch(attributeGet(attributes))
      dispatch(filterReplaceDashboard(dashboardFilters))
      dispatch(surveyActivate(activeSurvey))
    }
  }, [dispatch])

  function validate() {
    const picked = new DashboardFilters(store.getState().filter.dashboard).customFiltersIds
    //Le périmètre de l'établissement n'appartient pas à la fonction : il ne repart pas avec elle
    props.onValidate(picked.filter((id) => !props.lockedAttributeIds.includes(id)))
  }

  return (
    <Modal
      zIndex={1000}
      isFullScreen
      title={props.entityName}
      isCloseButtonVisible
      onClose={props.onClose}
      onNext={validate}
      onNextCta="Valider">
      <div className="flex items-center">
        <div className="grey-t flex-1">Sélectionnez les collaborateurs concernés à l’aide des filtres.</div>

        {props.surveys.length > 1 && (
          <div className="shrink-0">
            <ListDropdown
              isBold
              isCaretAlwaysVisible
              positionRight
              active={survey.id}
              value={survey.name}
              values={props.surveys}
              onSelect={(item: Survey) => setSurvey(item)}
            />
          </div>
        )}
      </div>

      {/* Filtres et résultats sur un même fond pleine largeur : on retrouve le tableau de bord */}
      <div
        className="modal-content-list mt-20"
        style={{ backgroundColor: "#f6f6f6", borderTop: "1px solid #CCCCCC", borderBottom: "1px solid #CCCCCC" }}>
        <div className="px-40 pb-20 pt-[10px]">
          <PageLoader status={isLoading ? STATUS_LOADING : STATUS_LOADED}>
            <DashboardFiltersWidget
              currentFilters={filters}
              currentSurvey={survey}
              hideBorder
              readonlyFilterIds={lockedFilterIds}
            />

            <div className="mt-20 [&_.card]:mb-0">
              <DashboardRpsOverviewWidget dashboardFilters={currentDashboardFilters} />
            </div>
          </PageLoader>
        </div>
      </div>
    </Modal>
  )
}
